import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, Calendar, Save, Trash2, Plus, X, ArrowLeft, Settings, Edit3 } from 'lucide-react';
import { LoadingOverlay } from './';
import { MeetingTypeConfigModal } from './MeetingTypeConfigModal';
import { SessionContext, MeetingType, SessionContextFormData } from '../types/sessionContext';
import { SessionContextService } from '../services/sessionContextService';
import { SessionService } from '../services';

export const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [sessionId] = useState<string>(() => SessionService.getSessionId());
  
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sessionContext, setSessionContext] = useState<SessionContext | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [meetingTypeSaving, setMeetingTypeSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  
  // Form state
  const [companyContext, setCompanyContext] = useState<string>('');
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [newMeetingType, setNewMeetingType] = useState<{ name: string; description: string }>({
    name: '',
    description: ''
  });
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingMeetingType, setEditingMeetingType] = useState<MeetingType | null>(null);

  useEffect(() => {
    const loadSessionContext = async () => {
      try {
        setLoading(true);
        setError('');
        const context = await SessionContextService.getSessionContext(sessionId);
        setSessionContext(context);
        setCompanyContext(context.companyContext || '');
        setMeetingTypes(context.meetingTypes || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load session context');
      } finally {
        setLoading(false);
      }
    };

    loadSessionContext();
  }, [sessionId]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Zapisz tylko company context - meeting types są zapisywane automatycznie
      const formData: SessionContextFormData = {
        companyContext: companyContext.trim(),
        meetingTypes: meetingTypes // Wymagane przez API, ale meeting types są już aktualne
      };

      const savedContext = await SessionContextService.saveSessionContext(sessionId, formData);
      setSessionContext(savedContext);
      setSuccess('Kontekst firmy został zapisany!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd podczas zapisywania kontekstu firmy');
    } finally {
      setSaving(false);
    }
  };

  const handleAddMeetingType = async () => {
    if (newMeetingType.name.trim()) {
      const newType: MeetingType = {
        id: `custom-${Date.now()}`,
        name: newMeetingType.name.trim(),
        description: newMeetingType.description.trim() || undefined
      };
      
      const updatedMeetingTypes = [...(Array.isArray(meetingTypes) ? meetingTypes : []), newType];
      
      try {
        setMeetingTypeSaving(true);
        setError('');
        setSuccess('');
        
        // Bezpośredni zapis do bazy
        const formData: SessionContextFormData = {
          companyContext: companyContext.trim(),
          meetingTypes: updatedMeetingTypes
        };

        const savedContext = await SessionContextService.saveSessionContext(sessionId, formData);
        setSessionContext(savedContext);
        setMeetingTypes(updatedMeetingTypes);
        setNewMeetingType({ name: '', description: '' });
        setSuccess('Nowy typ spotkania został dodany!');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Błąd podczas dodawania typu spotkania');
      } finally {
        setMeetingTypeSaving(false);
      }
    }
  };

  const handleRemoveMeetingType = async (idToRemove: string) => {
    const updatedMeetingTypes = Array.isArray(meetingTypes) ? meetingTypes.filter(type => type.id !== idToRemove) : [];
    
    try {
      setMeetingTypeSaving(true);
      setError('');
      setSuccess('');
      
      // Bezpośredni zapis do bazy
      const formData: SessionContextFormData = {
        companyContext: companyContext.trim(),
        meetingTypes: updatedMeetingTypes
      };

      const savedContext = await SessionContextService.saveSessionContext(sessionId, formData);
      setSessionContext(savedContext);
      setMeetingTypes(updatedMeetingTypes);
      setSuccess('Typ spotkania został usunięty!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd podczas usuwania typu spotkania');
    } finally {
      setMeetingTypeSaving(false);
    }
  };

  const handleUpdateMeetingType = (id: string, updates: Partial<MeetingType>) => {
    setMeetingTypes(Array.isArray(meetingTypes) ? meetingTypes.map(type => 
      type.id === id ? { ...type, ...updates } : type
    ) : []);
  };

  const handleEditMeetingType = (meetingType: MeetingType) => {
    setEditingMeetingType(meetingType);
    setIsModalOpen(true);
  };

  const handleCreateMeetingType = () => {
    setEditingMeetingType(null);
    setIsModalOpen(true);
  };

  const handleSaveMeetingType = async (meetingTypeData: Partial<MeetingType>) => {
    let updatedMeetingTypes: MeetingType[];
    
    if (editingMeetingType) {
      // Update existing meeting type
      updatedMeetingTypes = Array.isArray(meetingTypes) ? meetingTypes.map(type => 
        type.id === editingMeetingType.id ? { ...type, ...meetingTypeData } : type
      ) : [];
    } else {
      // Create new meeting type
      const newType: MeetingType = {
        id: `custom-${Date.now()}`,
        ...meetingTypeData,
        name: meetingTypeData.name || '',
        createdAt: new Date().toISOString()
      } as MeetingType;
      
      updatedMeetingTypes = [...(Array.isArray(meetingTypes) ? meetingTypes : []), newType];
    }
    
    try {
      setMeetingTypeSaving(true);
      setError('');
      setSuccess('');
      
      // Bezpośredni zapis do bazy
      const formData: SessionContextFormData = {
        companyContext: companyContext.trim(),
        meetingTypes: updatedMeetingTypes
      };

      const savedContext = await SessionContextService.saveSessionContext(sessionId, formData);
      setSessionContext(savedContext);
      setMeetingTypes(updatedMeetingTypes);
      setSuccess(editingMeetingType ? 'Typ spotkania został zaktualizowany!' : 'Nowy typ spotkania został utworzony!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Błąd podczas zapisywania typu spotkania');
    } finally {
      setMeetingTypeSaving(false);
    }
  };

  const handleClearAll = async () => {
    if (window.confirm('Czy na pewno chcesz wyczyścić wszystkie ustawienia? Ta operacja nie może zostać cofnięta.')) {
      try {
        setSaving(true);
        setError('');
        setSuccess('');
        
        await SessionContextService.deleteSessionContext(sessionId);
        
        // Reset form to defaults
        const defaultContext = await SessionContextService.getSessionContext(sessionId);
        setSessionContext(defaultContext);
        setCompanyContext('');
        setMeetingTypes(defaultContext.meetingTypes || []);
        setSuccess('Ustawienia zostały wyczyszczone!');
        
        setTimeout(() => setSuccess(''), 3000);
        
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to clear session context');
      } finally {
        setSaving(false);
      }
    }
  };

  if (loading) {
    return <LoadingOverlay />;
  }

  return (
    <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                title="Powrót do głównej"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold gradient-text mb-2">Ustawienia Organizacji</h1>
                <p className="text-gray-600 dark:text-gray-300">
                  Sesja: {sessionId.slice(-8)} • Skonfiguruj kontekst swojej firmy i typy spotkań
                </p>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleClearAll}
                disabled={saving}
                className="flex items-center space-x-2 px-4 py-2 bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30 
                           text-red-600 dark:text-red-400 rounded-xl transition-colors duration-200 disabled:opacity-50"
                title="Wyczyść wszystkie ustawienia"
              >
                <Trash2 className="w-5 h-5" />
                <span>Wyczyść</span>
              </button>
              
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl 
                           transition-colors duration-200 disabled:opacity-50"
              >
                <Save className="w-5 h-5" />
                <span>{saving ? 'Zapisywanie...' : 'Zapisz'}</span>
              </button>
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
              <p className="text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
              <p className="text-green-600 dark:text-green-400">{success}</p>
            </div>
          )}

          <div className="space-y-8">
            {/* Company Context Section */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-center space-x-3 mb-6">
                <Building className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                  Kontekst Firmy
                </h2>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Opis firmy i branży
                  </label>
                  <textarea
                    value={companyContext}
                    onChange={(e) => setCompanyContext(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl 
                               bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                               focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent
                               resize-none transition-colors"
                    placeholder="Opisz swoją firmę, branżę, produkty/usługi, wielkość zespołu, klientów docelowych...
Przykład: Jesteśmy firmą technologiczną specjalizującą się w rozwiązaniach SaaS dla małych i średnich przedsiębiorstw. Oferujemy system zarządzania projektami z zespołem 25 osób."
                  />
                </div>
                
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  💡 Te informacje pomogą AI lepiej zrozumieć kontekst Twoich rozmów sprzedażowych i dostarczyć bardziej trafne analizy.
                </div>
              </div>
            </div>

            {/* Meeting Types Section */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-center space-x-3 mb-6">
                <Calendar className="w-6 h-6 text-green-600 dark:text-green-400" />
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                  Typy Spotkań
                </h2>
              </div>
              
              {/* Existing Meeting Types */}
              <div className="space-y-3 mb-6">
                {Array.isArray(meetingTypes) && meetingTypes.map((meetingType) => (
                  <div key={meetingType.id} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100">{meetingType.name}</h3>
                          {(meetingType.dataExtractionFields || meetingType.agendaScript || (meetingType.successCriteria && meetingType.successCriteria.length > 0)) && (
                            <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                              Rozbudowany
                            </span>
                          )}
                        </div>
                        {meetingType.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{meetingType.description}</p>
                        )}
                        
                        {/* Show some expanded configuration preview */}
                        <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
                          {meetingType.dataExtractionFields && (
                            <span className="flex items-center space-x-1">
                              <span>📊</span>
                              <span>Dane CRM</span>
                            </span>
                          )}
                          {meetingType.successCriteria && Array.isArray(meetingType.successCriteria) && meetingType.successCriteria.length > 0 && (
                            <span className="flex items-center space-x-1">
                              <span>✅</span>
                              <span>{meetingType.successCriteria.length} kryteria</span>
                            </span>
                          )}
                          {meetingType.agendaScript && (
                            <span className="flex items-center space-x-1">
                              <span>📋</span>
                              <span>Agenda</span>
                            </span>
                          )}
                          {meetingType.processMapping && typeof meetingType.processMapping === 'string' && meetingType.processMapping.trim().length > 0 && (
                            <span className="flex items-center space-x-1">
                              <span>🔄</span>
                              <span>Proces</span>
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleEditMeetingType(meetingType)}
                          disabled={meetingTypeSaving}
                          className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50"
                          title="Konfiguruj typ spotkania"
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRemoveMeetingType(meetingType.id)}
                          disabled={meetingTypeSaving}
                          className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                          title="Usuń typ spotkania"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Add New Meeting Type */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Dodaj nowy typ spotkania
                  </h3>
                  <button
                    onClick={handleCreateMeetingType}
                    disabled={meetingTypeSaving}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors duration-200 disabled:opacity-50"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Zaawansowana konfiguracja</span>
                  </button>
                </div>
                
                <div className="space-y-3">
                  <input
                    type="text"
                    value={newMeetingType.name}
                    onChange={(e) => setNewMeetingType({ ...newMeetingType, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl 
                               bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                               focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                    placeholder="Nazwa typu spotkania"
                  />
                  
                  <input
                    type="text"
                    value={newMeetingType.description}
                    onChange={(e) => setNewMeetingType({ ...newMeetingType, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl 
                               bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                               focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                    placeholder="Opis (opcjonalny)"
                  />
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={handleAddMeetingType}
                      disabled={!newMeetingType.name.trim() || meetingTypeSaving}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl 
                                 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-4 h-4" />
                      <span>{meetingTypeSaving ? 'Dodawanie...' : 'Dodaj podstawowy'}</span>
                    </button>
                    
                    <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                      <span>lub użyj zaawansowanej konfiguracji powyżej</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modal for advanced meeting type configuration */}
          <MeetingTypeConfigModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSave={handleSaveMeetingType}
            meetingType={editingMeetingType}
            title={editingMeetingType ? 'Edytuj typ spotkania' : 'Nowy typ spotkania'}
          />
    </div>
  );
};